// Pure Samuel compute (runs inside the Web Worker): pitch tracking + model inference.
export const SAMPLE_RATE = 44100;
const SR = SAMPLE_RATE, SPF = 512, FMIN = 70, FMAX = 500, TARGET_RMS = 0.05;
export const FRAME_RATE = SR / SPF;
const NAMES = ['voiceness', 'intensity', 'tongueIndex', 'tongueDiameter', 'constrictionIndex', 'constrictionDiameter', 'lipDiameter'];

// YIN pitch on a 4× decimated signal, one value per control frame.
export const pitchTrack = (wav, nFrames) => {
  const D = 4, sr = SR / D, x = new Float32Array(Math.floor(wav.length / D));
  for (let i = 0; i < x.length; i++) { let s = 0; for (let k = 0; k < D; k++) s += wav[i * D + k]; x[i] = s / D; }
  const W = 512, tMin = Math.floor(sr / FMAX), tMax = Math.ceil(sr / FMIN);
  const f0 = new Float32Array(nFrames), voiced = new Uint8Array(nFrames), d = new Float32Array(tMax + 1);
  for (let f = 0; f < nFrames; f++) {
    const c = Math.floor((f * SPF + SPF / 2) / D), st = c - W / 2;
    if (st < 0 || st + W + tMax >= x.length) continue;
    let e = 0; for (let i = 0; i < W; i++) e += x[st + i] * x[st + i];
    if (e / W < 1e-6) continue;
    for (let t = 1; t <= tMax; t++) { let s = 0; for (let i = 0; i < W; i++) { const q = x[st + i] - x[st + i + t]; s += q * q; } d[t] = s; }
    let run = 0, best = -1;
    for (let t = 1; t <= tMax; t++) { run += d[t]; const v = run ? (d[t] * t) / run : 1; d[t] = v; }
    for (let t = tMin; t < tMax; t++) if (d[t] < 0.15) { while (t + 1 < tMax && d[t + 1] < d[t]) t++; best = t; break; }
    if (best > 0) {
      const a = d[best - 1], b = d[best], cc = d[best + 1], den = a - 2 * b + cc;
      const tt = best + (den ? (a - cc) / (2 * den) : 0);
      f0[f] = sr / tt; voiced[f] = 1;
    }
  }
  const idx = []; for (let i = 0; i < nFrames; i++) if (voiced[i]) idx.push(i);
  const out = new Float32Array(nFrames);
  if (!idx.length) out.fill((FMIN + FMAX) / 2);
  else for (let i = 0, j = 0; i < nFrames; i++) {
    while (j < idx.length - 1 && idx[j + 1] <= i) j++;
    const a = idx[j], b = idx[Math.min(j + 1, idx.length - 1)];
    out[i] = i <= a || a === b ? f0[a] : i >= b ? f0[b] : f0[a] + ((f0[b] - f0[a]) * (i - a)) / (b - a);
    out[i] = Math.min(FMAX, Math.max(FMIN, out[i]));
  }
  return { f0: out, voiced };
};

// Run Samuel: returns a response in the same shape as Samuel's /api/synthesize.
export const infer = async (ort, session, wav) => {
  const n = Math.ceil(wav.length / SPF);
  const x = new Float32Array(n * SPF); x.set(wav);
  let e = 0; for (let i = 0; i < wav.length; i++) e += wav[i] * wav[i];
  const g = TARGET_RMS / Math.sqrt(Math.max(e / Math.max(1, wav.length), 1e-12));
  for (let i = 0; i < x.length; i++) x[i] *= g;
  const out = await session.run({ wav: new ort.Tensor('float32', x, [1, 1, x.length]) });
  const data = out.params.data, T = out.params.dims[1];
  const params = Object.fromEntries(NAMES.map((k) => [k, new Array(n)]));
  for (let t = 0; t < n; t++) NAMES.forEach((k, j) => { params[k][t] = data[Math.min(t, T - 1) * NAMES.length + j]; });
  const { f0, voiced } = pitchTrack(wav, n);
  params.frequency = Array.from(f0);
  return { frame_rate: FRAME_RATE, n_frames: n, duration_s: wav.length / SR, params, voiced: Array.from(voiced, Boolean), source: 'samuel' };
};
